import { DEFAULT_Z_INDEX } from "constants/chart.constants";
import { ViolinCurvePoint } from "entities/charts/eva/translators/violinDataProcessing";

interface ViolinRenderData {
  curve: ViolinCurvePoint[];
  groupMaxDensity: number;
  degenerate: boolean;
  color: string;
  opacity: number;
}

// ECharts custom-series api contract this function depends on (ECharts 5.6):
//   api.value(0)          -> the datum's category value (the x-axis ordinal for this violin)
//   api.size([1, 0])[0]   -> the pixel width of one category band, used to size the silhouette
//   api.coord([cat, y])   -> [pixelX, pixelY] for a (category, value) pair in the coord system
// These are exercised only against hand-written stubs in the unit tests. A real-runtime smoke
// test (echarts.init(svg) in jsdom, feeding translated options through setOption) was prototyped
// and confirmed the runtime invokes this renderItem and it returns finite polygon geometry, but
// it was not kept: importing the full echarts graph under jest (echarts/zrender are force-
// transformed by babel) plus jsdom svg rendering pushed a single test past five minutes, which is
// unacceptable in CI. If this contract drifts across an ECharts upgrade, re-verify manually with
// that prototype. The three api calls above are the entire surface to check.
export function getViolinPlotPosition(api, data: ViolinRenderData) {
  const { curve, groupMaxDensity, degenerate, color, opacity } = data;
  const categoryValue = api.value(0);
  // The runtime can hand back unusable size/coord values in degraded coordinate
  // system states. A throw here takes the whole chart down through the React
  // error boundary, so skip the datum (render nothing) instead.
  const bandSize = api.size([1, 0]);
  const bandWidth = Array.isArray(bandSize) ? bandSize[0] : undefined;
  if (!Number.isFinite(bandWidth)) {
    return;
  }
  const halfMax = Math.max(0, (bandWidth * 0.8 - 2) / 2);

  // `degenerate` already covers both extra checks for every buildViolinGroup output. They are
  // kept defensively because renderData is a loose contract, not a ViolinGroup: curve.length < 2
  // avoids a one-point silhouette and groupMaxDensity <= 0 guards the density/groupMaxDensity
  // divide below from producing NaN geometry.
  if (degenerate || curve.length < 2 || groupMaxDensity <= 0) {
    const centre = api.coord([categoryValue, curve[0]?.value ?? 0]);
    if (!Array.isArray(centre)) {
      return;
    }
    const [cx, cy] = centre;
    return {
      type: "line",
      shape: {
        x1: cx - halfMax,
        y1: cy,
        x2: cx + halfMax,
        y2: cy
      },
      z: DEFAULT_Z_INDEX,
      style: {
        stroke: color,
        lineWidth: 2,
        opacity
      }
    };
  }

  const leftEdge: number[][] = [];
  const rightEdge: number[][] = [];
  const lastIndex = curve.length - 1;
  for (let index = 0; index < curve.length; index++) {
    const { value, density } = curve[index];
    const pixel = api.coord([categoryValue, value]);
    if (!Array.isArray(pixel)) {
      return;
    }
    const [cx, cy] = pixel;
    // Pinch the silhouette to zero width at the clamped extremes so it tapers
    // instead of ending blunt (pure cosmetic; the grid stays clamped).
    const isExtreme = index === 0 || index === lastIndex;
    const halfW = isExtreme ? 0 : (density / groupMaxDensity) * halfMax;
    leftEdge.push([cx - halfW, cy]);
    rightEdge.push([cx + halfW, cy]);
  }

  return {
    type: "polygon",
    shape: {
      points: [...leftEdge, ...rightEdge.reverse()]
    },
    z: DEFAULT_Z_INDEX,
    style: {
      fill: color,
      opacity,
      stroke: color
    }
  };
}
